import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { useState, type FormEvent } from 'react';

import { AutoTextarea } from '@/components/admin/forms/AutoTextarea';
import { CoverImageField } from '@/components/admin/forms/CoverImageField';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { trackUmamiEvent } from '@/lib/analytics';

export interface Event {
  id: number;
  title: string;
  description: string | null;
  location: string | null;
  category: string | null;
  startsAt: string | null;
  endsAt: string | null;
  imageUrl: string | null;
}

interface EventEditorProps {
  event: Event | null;
  categories: string[];
  [key: string]: unknown;
}

const labelClass = 'text-on-surface mb-1.5 block text-sm font-medium';
const panelLabel = 'text-on-surface-light/70 mb-3 text-[10px] font-bold tracking-[0.1em] uppercase';
const dateInputClass =
  'bg-surface border-line text-on-surface focus:border-primary w-full rounded-md border px-3 py-2 text-sm transition-all outline-none focus:shadow-[0_0_0_3px_rgb(from_var(--color-primary)_r_g_b/0.12)]';

// Derived schedule state from the chosen dates.
function eventState(
  start: string,
  end: string,
): { label: string; icon: string; className: string } {
  if (!start) {
    return {
      label: 'Sem data',
      icon: 'edit',
      className: 'text-on-surface-light border-line-strong bg-background',
    };
  }
  const now = Date.now();
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : startMs;
  if (now < startMs) {
    return {
      label: 'Próximo',
      icon: 'event',
      className: 'text-tertiary border-tertiary/40 bg-tertiary/10',
    };
  }
  if (now <= endMs) {
    return {
      label: 'A decorrer',
      icon: 'schedule',
      className: 'text-primary border-primary/40 bg-primary/8',
    };
  }
  return {
    label: 'Terminado',
    icon: 'event',
    className: 'text-on-surface-light border-line-strong bg-background',
  };
}

// Human-readable "when" preview, e.g. "sáb, 12 jul 2026 · 15:00–23:00".
function formatWhen(start: string, end: string): string | null {
  if (!start) return null;
  const s = new Date(start);
  const date = s.toLocaleDateString('pt-PT', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const time = s.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  if (!end) return `${date} · ${time}`;
  const e = new Date(end);
  const eTime = e.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  if (e.toDateString() === s.toDateString()) return `${date} · ${time}–${eTime}`;
  const eDate = e.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
  return `${date} ${time} → ${eDate} ${eTime}`;
}

export default function EventEditor() {
  const { event, categories } = usePage<EventEditorProps>().props;
  const isEdit = Boolean(event);

  const categoryOptions = [
    { label: '— sem categoria —', value: '' },
    ...categories.map((c) => ({ label: c, value: c })),
  ];

  const [coverPreview, setCoverPreview] = useState<string | null>(event?.imageUrl ?? null);

  const form = useForm({
    event: {
      title: event?.title ?? '',
      description: event?.description ?? '',
      location: event?.location ?? '',
      category: event?.category ?? '',
      starts_at: event?.startsAt ? event.startsAt.slice(0, 16) : '',
      ends_at: event?.endsAt ? event.endsAt.slice(0, 16) : '',
    },
    cover_image: null as File | null,
  });
  const { data, setData, processing } = form;
  const e = form.errors as Record<string, string | undefined>;

  form.transform((d) => {
    const eventData = { ...d.event, category: d.event.category || null };
    const out: Record<string, unknown> = { event: eventData };
    if (d.cover_image) out.cover_image = d.cover_image;
    return out;
  });

  function setField(key: keyof typeof data.event, value: string) {
    setData('event', { ...data.event, [key]: value });
  }

  function handleCover(file: File | null) {
    setData('cover_image', file);
    setCoverPreview(file ? URL.createObjectURL(file) : (event?.imageUrl ?? null));
  }

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const opts = { forceFormData: !!data.cover_image };
    trackUmamiEvent(event ? 'admin_content_update' : 'admin_content_create', {
      content_type: 'event',
    });
    if (event) form.patch(`/events/${event.id}`, opts);
    else form.post('/events', opts);
  }

  const state = eventState(data.event.starts_at, data.event.ends_at);
  const whenPreview = formatWhen(data.event.starts_at, data.event.ends_at);
  const crumb = data.event.title.trim() || (isEdit ? 'Sem título' : 'Rascunho sem título');

  return (
    <>
      <Head title={isEdit ? 'Editar evento — Administração' : 'Novo evento — Administração'} />

      <form onSubmit={submit}>
        {/* Top bar */}
        <div className="border-line-strong bg-surface flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 shadow-[0_14px_34px_-28px_rgba(33,29,24,0.5)]">
          <Link
            href="/events"
            className="text-on-surface-light hover:text-primary inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Eventos
          </Link>
          <div className="border-line flex flex-col border-l pl-3.5 leading-tight">
            <span className="text-secondary text-[10px] font-bold tracking-[0.12em] uppercase">
              {isEdit ? 'Editar evento' : 'Novo evento'}
            </span>
            <span className="font-headline text-on-surface max-w-[40ch] truncate text-base font-semibold">
              {crumb}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2.5">
            <Link
              href="/events"
              className="bg-surface border-line-strong text-on-surface hover:border-primary hover:text-primary inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold transition-colors duration-200"
            >
              Cancelar
            </Link>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={processing}
              loading={processing}
            >
              {!processing && <span className="material-symbols-outlined text-[18px]">save</span>}
              {processing ? 'A guardar…' : 'Guardar'}
            </Button>
          </div>
        </div>

        {/* Two-zone editor */}
        <div className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-[1fr_320px]">
          {/* Main column */}
          <div className="space-y-4">
            <div className="surface-card px-5 py-4">
              <input
                type="text"
                value={data.event.title}
                onChange={(ev) => setField('title', ev.target.value)}
                required
                placeholder="Título do evento"
                aria-label="Título"
                className="font-headline text-on-surface placeholder:text-on-surface-light/40 w-full border-0 bg-transparent text-2xl leading-tight font-semibold outline-none sm:text-3xl"
              />
              <div className="text-on-surface-light/80 mt-2.5 flex items-center gap-2 text-[13px]">
                <span className="material-symbols-outlined text-on-surface-light/60 text-[18px]">
                  location_on
                </span>
                <input
                  type="text"
                  value={data.event.location ?? ''}
                  onChange={(ev) => setField('location', ev.target.value)}
                  aria-label="Local"
                  placeholder="Local do evento (opcional)"
                  className="border-line-strong text-on-surface focus:border-primary min-w-[16rem] flex-1 border-0 border-b border-dashed bg-transparent px-0.5 py-0.5 outline-none"
                />
              </div>
              {e.title && <p className="text-error mt-1.5 text-xs">{e.title}</p>}
              {e.location && <p className="text-error mt-1 text-xs">{e.location}</p>}
            </div>

            <div className="surface-card px-5 py-4">
              <label className={labelClass} htmlFor="event-description">
                Descrição
              </label>
              <AutoTextarea
                id="event-description"
                value={data.event.description ?? ''}
                onChange={(ev) => setField('description', ev.target.value)}
                rows={6}
                placeholder="Descrição do evento (opcional)…"
              />
              {e.description && <p className="text-error mt-1 text-xs">{e.description}</p>}
            </div>
          </div>

          {/* Metadata aside */}
          <aside className="space-y-4 lg:sticky lg:top-4">
            <div className="surface-card px-4 py-4">
              <div className="mb-3 flex items-center justify-between">
                <span className={`${panelLabel} mb-0`}>Quando</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase ${state.className}`}
                >
                  <span className="material-symbols-outlined text-[13px]">{state.icon}</span>
                  {state.label}
                </span>
              </div>

              <div className="mb-3">
                <label className={labelClass} htmlFor="event-starts">
                  Início <span className="text-error">*</span>
                </label>
                <input
                  id="event-starts"
                  type="datetime-local"
                  value={data.event.starts_at}
                  onChange={(ev) => setField('starts_at', ev.target.value)}
                  required
                  className={dateInputClass}
                />
                {e.starts_at && <p className="text-error mt-1 text-xs">{e.starts_at}</p>}
              </div>

              <div>
                <label className={labelClass} htmlFor="event-ends">
                  Fim
                </label>
                <input
                  id="event-ends"
                  type="datetime-local"
                  value={data.event.ends_at}
                  onChange={(ev) => setField('ends_at', ev.target.value)}
                  className={dateInputClass}
                />
                {e.ends_at && <p className="text-error mt-1 text-xs">{e.ends_at}</p>}
              </div>

              {whenPreview && (
                <div className="border-line bg-background text-on-surface mt-3 flex items-center gap-2 rounded-md border px-2.5 py-2 text-[12.5px]">
                  <span className="material-symbols-outlined text-secondary text-[17px]">
                    calendar_month
                  </span>
                  {whenPreview}
                </div>
              )}
            </div>

            <div className="surface-card px-4 py-4">
              <span className={panelLabel}>Detalhes</span>
              <span className="text-on-surface mb-1.5 block text-sm font-medium">Categoria</span>
              <Select
                options={categoryOptions}
                value={data.event.category ?? ''}
                onChange={(v) => setField('category', v)}
                placeholder="— sem categoria —"
                className="w-full"
              />
              {e.category && <p className="text-error mt-1 text-xs">{e.category}</p>}
            </div>

            <div className="surface-card px-4 py-4">
              <span className={panelLabel}>Imagem de capa</span>
              <CoverImageField
                previewUrl={coverPreview}
                onSelect={handleCover}
                onRemove={data.cover_image ? () => handleCover(null) : undefined}
              />
              {e.cover_image && <p className="text-error mt-1 text-xs">{e.cover_image}</p>}
            </div>
          </aside>
        </div>
      </form>
    </>
  );
}
